import * as React from 'react';

import {type Line} from '../../util/plotHelpers/types';
import {
  isValidGroupingDepth,
  useLinePlotGroupingDepthDynamicConfig,
} from '../../util/rampFeatureFlags/linePlotDynamicConfigs';
import PlotWarning from '../PlotWarning';
import {MULTI_METRIC_MAX_RUNS} from './defaults';
import {usePanelConfigContext} from './PanelConfigContext';
import {usePanelGroupingSettings} from './RunsLinePlotContext/usePanelGroupingSettings';

function HistogramMultiRunWarning({lines}: {lines: Line[]}) {
  if (lines.length === 0) {
    return null;
  }

  const firstRun = lines[0].run;
  return (
    <PlotWarning
      type="histogram-multi-runs"
      message={`Showing histogram for ${
        firstRun?.displayName || 'the first run'
      }`}
    />
  );
}

/**
 * Warning priority — first match returns:
 *
 *   1. histogram w/ >1 line
 *      Histogram renderer only draws lines[0]; the rest would be silently dropped.
 *
 *   2. multi-metric throttled (metrics × maxRuns > MULTI_METRIC_MAX_RUNS)
 *      Per-metric line count is reduced to fit the overall budget.
 *
 *   3. grouped + hasSubsampledGroups
 *      Backend reports at least one group had runs excluded from aggregation.
 *      Authoritative signal from the grouped query pipeline.
 *
 *   4. multi-runset (runsetCount > 1) + any runset hit its cap
 *      Reports can have multiple runsets; the backend query applies the cap
 *      **per runset**. When maxFetchedRunCount (max count across runsets)
 *      reaches the cap the single-runset wording "Showing up to N runs"
 *      is misleading — the user may be seeing N × runsetCount lines. The
 *      "per run set" variant clarifies which knob moves the cap. Applies to
 *      both grouped and ungrouped modes.
 *
 *   5. grouped + runCount >= maxGroupRuns
 *      In client-side grouping the frontend fetches a pool of up to
 *      maxGroupRuns raw runs and forms groups from them; a filled pool means
 *      the groups were built from a sample. `>=` tolerates any overshoot
 *      (e.g. baseline/pinned runs injected above the cap or cross-runset
 *      summation) so the warning isn't silently dropped.
 *
 *   6. grouped + lines.length > maxRuns
 *      More groups exist than the panel can display as lines.
 *
 *   7. ungrouped + runCount >= maxRuns
 *      Query hit its row limit. Without a backend project total we can't
 *      distinguish this from "exactly maxRuns matching runs", so the warning
 *      fires in both cases — helpText "raise the max" works either way.
 */
export const LinePlotLegendWarning = React.memo(
  ({
    disallowSelect,
    hasSubsampledGroups,
    isHistogram,
    isMultiMetric,
    isReadOnly,
    lines,
    loading,
    maxFetchedRunCount,
    maxGroupRuns,
    metrics,
    runCount,
    runsetCount,
    totalGroupedIncludedRuns,
  }: {
    disallowSelect: boolean;
    hasSubsampledGroups: boolean;
    isHistogram: boolean;
    isMultiMetric: boolean;
    isReadOnly: boolean;
    lines: Line[];
    loading: boolean;
    maxFetchedRunCount: number;
    maxGroupRuns: number;
    metrics: number;
    runCount: number;
    runsetCount: number;
    totalGroupedIncludedRuns: number;
  }) => {
    const {isAnyRunsetGrouped: isGrouped} = usePanelGroupingSettings();
    const {
      groupingDepth,
      isFullFidelity,
      limit: maxRuns,
    } = usePanelConfigContext();
    const dynamicConfigGroupingDepth = useLinePlotGroupingDepthDynamicConfig();

    const lineCountExceedsMaxRuns = lines.length > maxRuns;
    const isGroupingDepthEnabled =
      isFullFidelity &&
      isValidGroupingDepth(dynamicConfigGroupingDepth) &&
      isValidGroupingDepth(groupingDepth);
    const isThrottledMultiMetrics =
      isMultiMetric && metrics * maxRuns > MULTI_METRIC_MAX_RUNS;

    // Histograms cannot meaningfully overlay multiple runs' distributions on a
    // single chart — the renderer picks lines[0] and discards the rest. Gate
    // on lines.length (what the panel is actually rendering) rather than on
    // totalRuns (the project-wide count), which fired spuriously when a panel
    // was capped to a single line on a many-run project.
    if (isHistogram && lines.length > 1) {
      return <HistogramMultiRunWarning lines={lines} />;
    }

    if (isThrottledMultiMetrics && (!loading || lines.length > 0)) {
      const visualizedRuns = Math.floor(MULTI_METRIC_MAX_RUNS / metrics);
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to adjust the number of visualized metrics`
              : undefined
          }
          message={`Limited to ${visualizedRuns} runs for each visualized metric`}
          disallowSelect={disallowSelect}
          type="multi-metric"
        />
      );
    }

    // per-group subsampling detected from backend totalRuns/includedRuns fields
    if (isGrouped && hasSubsampledGroups && !isGroupingDepthEnabled) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Not all runs in every group were included in the aggregation.`
              : undefined
          }
          message={`Only ${totalGroupedIncludedRuns} runs are grouped`}
          disallowSelect={disallowSelect}
          type="group-subsampling"
        />
      );
    }

    // Multi-runset per-runset cap: reports can stack multiple runsets into one
    // panel, and the backend query applies maxRuns/maxGroupRuns **per runset**.
    // The rendered panel is the union, so `runCount` (cross-runset sum) can
    // hit the cap when no individual runset has. Keying off maxFetchedRunCount
    // (max over per-runset counts) fires only when at least one runset is
    // actually capped, and the "per run set" wording clarifies which knob
    // moves the limit. Ordered before the single-runset branches so the
    // multi-runset phrasing wins when both would match.
    const isMultiRunset = runsetCount > 1;
    if (isMultiRunset && isGrouped && maxFetchedRunCount >= maxGroupRuns) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to change the number of runs sampled in group metrics`
              : undefined
          }
          message={`Showing groups from up to ${maxGroupRuns} runs per run set`}
          disallowSelect={disallowSelect}
          type="grouped-runs-exceed-max-runs"
        />
      );
    }

    if (isMultiRunset && !isGrouped && maxFetchedRunCount >= maxRuns) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to change the max number of runs displayed`
              : undefined
          }
          message={`Showing up to ${maxRuns} runs per run set`}
          disallowSelect={disallowSelect}
          type="number-of-runs-exceed-max-runs"
        />
      );
    }

    // Client-grouping sample-pool cap: in client-side grouping mode the frontend
    // fetches a pool of up to maxGroupRuns raw runs and forms groups from them.
    // If we filled the pool, more matching runs likely exist in the project —
    // the visible groups were assembled from a sample, not the full population.
    // (Backend-grouping subsampling is handled above via hasSubsampledGroups.)
    if (isGrouped && runCount >= maxGroupRuns) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to change the number of runs sampled in group metrics`
              : undefined
          }
          message={`Showing groups from up to ${maxGroupRuns} runs`}
          disallowSelect={disallowSelect}
          type="grouped-runs-exceed-max-runs"
        />
      );
    }

    // show user warning about max groups if the total groups visible in the table exceed the maximum number of runs displayed in the chart--note this happens less often and is easier to notice than the previous case
    if (isGrouped && lineCountExceedsMaxRuns) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to change the max number of groups displayed`
              : undefined
          }
          message={`Showing first ${maxRuns} groups`}
          disallowSelect={disallowSelect}
          type="grouped-runs-exceed-max-groups"
        />
      );
    }

    /**
     * Ungrouped cap indicator: the backend query limit is maxRuns, so when
     * runCount hits maxRuns the query was capped. Without a backend project-
     * total count, we can't distinguish "capped with more available" from
     * "exactly this many runs match" — this fires in both cases. The helpText
     * ("raise the max") is a valid action either way, keeping false-positive
     * cost low.
     */
    if (!isGrouped && runCount >= maxRuns) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to change the max number of runs displayed`
              : undefined
          }
          message={`Showing up to ${maxRuns} runs`}
          disallowSelect={disallowSelect}
          type="number-of-runs-exceed-max-runs"
        />
      );
    }

    return null;
  }
);

LinePlotLegendWarning.displayName = 'LinePlotLegendWarning';
